import { observer } from "mobx-react-lite"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { forwardRef, useMemo, useRef } from "react"
import styles from "./styles.module.scss"
import { Control } from "react-hook-form"
import FormTextInput from "../FormTextInput"
import ArrowBackOutline from "~icons/eva/arrow-back-outline"
import ArrowForwardOutline from "~icons/eva/arrow-forward-outline"
import CalendarIcon from "~icons/eva/calendar-outline"
import {
  datesFormats,
  formatDateToString,
  formatLocalDate,
  getDateRangeForAgeGroup,
} from "@cruisen/react-shared"
import { isValidDate } from "../CruiseDatePicker/utils"
import { makeAutoObservable } from "mobx"
import classNames from "classnames"
import CloseModalButton from "../../buttons/CloseModalButton"
import { CruisePassengerAgeGroup } from "@cruisen/contract"

const months = new Map<number, string>([
  [0, "January"],
  [1, "February"],
  [2, "March"],
  [3, "April"],
  [4, "May"],
  [5, "June"],
  [6, "July"],
  [7, "August"],
  [8, "September"],
  [9, "October"],
  [10, "November"],
  [11, "December"],
])

const getYears = (minYear: number, maxYear: number, inOrder: boolean) => {
  const years = []
  for (let i = minYear; i <= maxYear; i++) {
    years.push(i)
  }
  return inOrder ? years : years.reverse()
}

const CustomInput = forwardRef(
  (
    {
      onClick,
      ...props
    }: {
      onClick?: () => void
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      control?: Control<any, any>
      inputName?: string
      label?: string
      parentBackgroundColor?: string
      lightOutline?: boolean
      isDisabled?: boolean
      testID?: string
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    return (
      <div ref={ref}>
        <FormTextInput
          name={props.inputName}
          control={props.control}
          label={props.label}
          className={styles.dateInput}
          parentBackgroundColor={props.parentBackgroundColor}
          lightOutline={props.lightOutline}
          onClick={onClick}
          disabled={props.isDisabled}
          readOnly
          iconRight={<CalendarIcon className={styles.calendarIcon} />}
          testID={props.testID}
        />
      </div>
    )
  },
)

class FormDatePickerStore {
  selectedDate: Date | null
  openToDate: Date | null
  activeModal: "month" | "year" | null

  constructor(defaultDate: string, defaultOpenToDate?: string) {
    this.selectedDate = defaultDate ? new Date(defaultDate) : null
    this.openToDate = defaultOpenToDate
      ? new Date(defaultOpenToDate)
      : this.selectedDate
        ? this.selectedDate
        : null
    this.activeModal = null

    makeAutoObservable(this)
  }

  setSelectedDate(value: Date | null) {
    this.selectedDate = value
  }

  setOpenToDate(value: Date | null) {
    this.openToDate = value
  }

  setActiveModal(value: "month" | "year" | null) {
    this.activeModal = value
  }
}

export const FormDatePicker = observer(function FormDatePicker({
  onChange,
  control,
  inputName,
  label,
  parentBackgroundColor,
  lightOutline = false,
  defaultDate,
  defaultOpenToDate,
  inOrder,
  enabledDateRange,
  ageGroup,
  isDisabled,
  forceEdit = false,
  testID,
  selectMonthYearDropdown = false,
}: {
  onChange: (date: string) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any, any>
  inputName: string
  label: string
  parentBackgroundColor: string
  lightOutline?: boolean
  defaultDate: string
  defaultOpenToDate?: string
  inOrder: boolean
  forceEdit?: boolean
  enabledDateRange?: {
    minDate?: Date
    maxDate?: Date
  }
  ageGroup?: CruisePassengerAgeGroup
  isDisabled: boolean
  testID?: string
  selectMonthYearDropdown?: boolean
}) {
  const store = useMemo(() => new FormDatePickerStore(defaultDate, defaultOpenToDate), [])
  const datePickerRef = useRef<DatePicker>(null)

  const handleDateChange = (date: Date | null) => {
    store.setSelectedDate(date)
    const formattedDate = isValidDate(date) ? formatDateToString(date, "MM/dd/yyyy") : ""

    onChange(formattedDate)
    if (forceEdit) {
      datePickerRef.current?.setOpen?.(false)
    }
  }

  const { minDate, maxDate } = ageGroup
    ? getDateRangeForAgeGroup(ageGroup)
    : { minDate: enabledDateRange?.minDate, maxDate: enabledDateRange?.maxDate }

  const minYear = minDate?.getFullYear() || new Date().getFullYear() - 100
  const maxYear = maxDate?.getFullYear() || new Date().getFullYear() + 100
  const years = getYears(minYear, maxYear, inOrder)

  return (
    <DatePicker
      ref={datePickerRef}
      selected={store.selectedDate}
      disabled={isDisabled}
      openToDate={store.openToDate}
      disabledKeyboardNavigation
      onChange={handleDateChange}
      minDate={minDate}
      maxDate={maxDate}
      shouldCloseOnSelect={false}
      formatWeekDay={(date) => date.toString().slice(0, 3)}
      calendarClassName={styles.calendar}
      popperClassName={styles.popper}
      weekDayClassName={() => styles.weekDay}
      wrapperClassName={styles.wrapper}
      dayClassName={(day) => {
        const classes = [styles.day]
        const dayTime = day.getTime()
        const selectedDate = store.selectedDate?.getTime()

        if (selectedDate && dayTime === selectedDate) {
          classes.push(styles.selectedDay)
        }

        return classNames(classes)
      }}
      customInput={
        <CustomInput
          control={control}
          label={label}
          inputName={inputName}
          parentBackgroundColor={parentBackgroundColor}
          lightOutline={lightOutline}
          isDisabled={isDisabled}
          testID={testID}
        />
      }
      renderCustomHeader={({
        monthDate,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => (
        <div className={styles.header}>
          <button
            className={styles.arrowButton}
            onClick={decreaseMonth}
            disabled={prevMonthButtonDisabled}
          >
            <ArrowBackOutline className={styles.icon} />
          </button>
          {selectMonthYearDropdown ? (
            <div className={styles.monthYearDropdown}>
              <button
                onClick={() => store.setActiveModal(store.activeModal === "month" ? null : "month")}
                className={styles.dropdownButton}
              >
                <span>
                  {
                    formatLocalDate(monthDate.toISOString(), datesFormats.monthWithYear).split(
                      " ",
                    )[0]
                  }
                </span>
              </button>
              <button
                onClick={() => store.setActiveModal(store.activeModal === "year" ? null : "year")}
                className={styles.dropdownButton}
              >
                <span>{monthDate.getFullYear()}</span>
              </button>
            </div>
          ) : (
            <span>{formatLocalDate(monthDate.toISOString(), datesFormats.monthWithYear)}</span>
          )}
          <button
            className={styles.arrowButton}
            onClick={increaseMonth}
            disabled={nextMonthButtonDisabled}
          >
            <ArrowForwardOutline className={styles.icon} />
          </button>
        </div>
      )}
    >
      {store.activeModal === "month" && (
        <div className={styles.monthDropdown}>
          <div className={styles.dropdownHeader}>
            <p>Month</p>
          </div>
          <CloseModalButton
            onClick={() => store.setActiveModal(null)}
            className={styles.closeButton}
          />
          <div className={styles.monthList}>
            {Array.from(months.entries()).map(([key, value]) => (
              <button
                key={key}
                onClick={() => {
                  store.setOpenToDate(
                    store.openToDate
                      ? new Date(store.openToDate.getFullYear(), key, 1)
                      : new Date(new Date().getFullYear(), key, 1),
                  )
                  store.setActiveModal(null)
                }}
                className={styles.option}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
      )}
      {store.activeModal === "year" && (
        <div className={styles.yearDropdown}>
          <div className={styles.dropdownHeader}>
            <p>Year</p>
          </div>
          <CloseModalButton
            onClick={() => store.setActiveModal(null)}
            className={styles.closeButton}
          />
          <div className={styles.yearList}>
            {years.map((year) => (
              <button
                key={year}
                onClick={() => {
                  store.setOpenToDate(
                    store.openToDate
                      ? new Date(year, store.openToDate.getMonth(), 1)
                      : new Date(year, new Date().getMonth(), 1),
                  )
                  store.setActiveModal(null)
                }}
                className={styles.option}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
      )}
    </DatePicker>
  )
})
